import { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldCheck, Trash2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StaleCompaniesPanel } from "@/components/StaleCompaniesPanel";

const statusTone = {
  active: "border-green-200 bg-green-50 text-green-800",
  payment_required: "border-amber-200 bg-amber-50 text-amber-900",
  past_due: "border-red-200 bg-red-50 text-red-800",
  canceled: "border-slate-200 bg-slate-50 text-slate-600",
};

export default function SuperAdmin() {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [demo, setDemo] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [c, d] = await Promise.all([api.get("/admin/companies"), api.get("/admin/demo-data")]);
    setCompanies(c.data);
    setDemo(d.data);
  }, []);

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e)));
  }, [load]);

  const toggleFree = async (company) => {
    try {
      await api.post(`/admin/companies/${company.key}/free-access`, { enabled: !company.free_access });
      toast.success(t("admin.saved"));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const demoCount = demo
    ? demo.users.length + demo.customers.length + demo.jobs.length + demo.price_items.length
    : 0;

  const purge = async () => {
    if (!window.confirm(t("admin.purgeConfirm", { count: demoCount }))) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/demo-data/purge");
      toast.success(t("admin.purged", { count: Object.values(data.removed || {}).reduce((a, b) => a + b, 0) }));
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="superadmin-page">
      <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900 sm:text-3xl">
        <ShieldCheck className="h-6 w-6 text-blue-600" /> {t("admin.title")}
      </h1>
      <p className="text-sm text-slate-500">{t("admin.subtitle")}</p>

      <div className="mt-4 space-y-2" data-testid="admin-companies">
        {companies.map((c) => (
          <div key={c.key} data-testid={`admin-company-${c.key}`} className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Building2 className="h-4 w-4 text-slate-400" />
              <span className="text-base font-semibold text-slate-900">{c.name}</span>
              <span className={`rounded border px-1.5 py-0.5 text-[11px] font-semibold ${statusTone[c.status] || statusTone.payment_required}`}>
                {t(`sub.status.${c.status}`)}
              </span>
              {c.free_access && (
                <span className="rounded border border-teal-300 bg-teal-50 px-1.5 py-0.5 text-[11px] font-semibold text-teal-800">
                  {t("sub.freeAccess")}
                </span>
              )}
              <span className="ml-auto text-sm text-slate-600" data-testid={`admin-company-users-${c.key}`}>
                {c.users} / {c.max_users || 0} {t("admin.users")}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
              <span>{c.plan_name || t("sub.noPlan")}</span>
              {c.plan_price && <span>{c.plan_price} kr/mån</span>}
              {c.current_period_end && <span>{t("admin.periodEnd")}: {c.current_period_end.slice(0, 10)}</span>}
              {c.org_no && <span>{c.org_no}</span>}
            </div>
            <Button
              size="sm"
              variant="outline"
              data-testid={`admin-free-access-${c.key}`}
              onClick={() => toggleFree(c)}
              className="mt-2 h-9 bg-white transition-colors duration-200 hover:bg-slate-100"
            >
              {c.free_access ? t("admin.revokeFree") : t("admin.grantFree")}
            </Button>
          </div>
        ))}
      </div>

      <StaleCompaniesPanel onChanged={load} />

      <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50/70 p-4" data-testid="admin-demo-panel">
        <h2 className="text-xl font-semibold text-slate-800">{t("admin.demoTitle")}</h2>
        <p className="mt-1 text-sm text-slate-600">{t("admin.demoHint")}</p>
        {demo && (
          <ul className="mt-2 space-y-1 text-sm text-slate-700" data-testid="admin-demo-list">
            <li>{t("admin.demoUsers")}: {demo.users.map((u) => u.email).join(", ") || t("common.none")}</li>
            <li>{t("admin.demoCustomers")}: {demo.customers.map((c) => c.name).join(", ") || t("common.none")}</li>
            <li>
              {t("admin.demoJobs")}:{" "}
              {demo.jobs.map((j) => `#${j.order_number} ${j.title}`).join(", ") || t("common.none")}
            </li>
            <li>{t("admin.demoPrices")}: {demo.price_items.map((p) => p.name).join(", ") || t("common.none")}</li>
          </ul>
        )}
        <Button
          data-testid="admin-purge-demo"
          disabled={busy || demoCount === 0}
          onClick={purge}
          className="mt-3 h-11 bg-red-600 text-white transition-colors duration-200 hover:bg-red-700"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (
            <>
              <Trash2 className="mr-1.5 h-4 w-4" /> {t("admin.purgeBtn", { count: demoCount })}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
